// -------------------------------------------------------------------------------------------
// The goal of this file is to allow asynchronous importing of DevExpress components.
// If you import all classes directly in your components, DevExpress will be included in your bundle.
// But DevExpress components are huge (~600KB gzipped), so we want to load them asynchronously.
// -------------------------------------------------------------------------------------------
// HOW TO USE:
// 1. import DevExpress components ONLY from this file (never from 'devextreme' directly)
// 2. For components use `<DXG.component_name />` (e.g. `<DXG.Button />`), for instantiation use use `new DXG.class_name()`

import type dxDataGrid from 'devextreme/ui/data_grid';
import type { DataSourceLike } from 'devextreme/data/data_source';
import type {
  SavingEvent,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  EditingStartEvent,
  InitNewRowEvent,
  ToolbarPreparingEvent,
  ToolbarPreparingInfo,
} from 'devextreme/ui/data_grid';
import type { DataChange } from 'devextreme/common/grids';
import type { EventInfo } from 'devextreme/events';
import type { Autocomplete } from 'devextreme-react/autocomplete';
import type { DataGrid, CheckBox, DateBox } from 'devextreme-react';
import type {
  Column,
  Paging,
  Editing,
  Lookup,
  Button,
  RequiredRule,
  SearchPanel,
  Sorting,
  AsyncRule,
  CustomRule,
  IEditingProps,
  DataGridRef,
} from 'devextreme-react/data-grid';
import { useDataSource } from '../dx-data-source';
export type {
  DataGridRef as DataGrid,
  CheckBox,
  ToolbarPreparingInfo,
  EventInfo,
  dxDataGrid,
  IEditingProps,
  SavingEvent,
  RowInsertingEvent,
  RowRemovingEvent,
  RowUpdatingEvent,
  EditingStartEvent,
  InitNewRowEvent,
  ToolbarPreparingEvent,
  DataSourceLike,
  DataChange,
};
export const DXG: {
  Button: typeof Button;
  RequiredRule: typeof RequiredRule;
  SearchPanel: typeof SearchPanel;
  Sorting: typeof Sorting;
  Column: typeof Column;
  Paging: typeof Paging;
  Editing: typeof Editing;
  Lookup: typeof Lookup;
  AsyncRule: typeof AsyncRule;
  CustomRule: typeof CustomRule;
  Autocomplete: typeof Autocomplete;
  CheckBox: typeof CheckBox;
  DataGrid: typeof DataGrid;
  DateBox: typeof DateBox;
} = {} as any;
let _promise: Promise<any> | null;
let _isLoaded: boolean;
let _error: any;

export function useDataGrid() {
  useDataSource();
  if (!_isLoaded) {
    if (!_promise) {
      _promise = initDataGrid();
    }
    // will trigger Suspense
    throw _promise;
  }
  if (_error) {
    throw _error;
  }
}

function initDataGrid() {
  return Promise.all([
    import('devextreme-react')
      .then((x) => {
        DXG.DataGrid = x.DataGrid;
        DXG.CheckBox = x.CheckBox;
        DXG.DateBox = x.DateBox;
      })
      .catch((e) => console.error('initDataGrid', e)),
    import('devextreme-react/data-grid').then((x) => {
      DXG.Button = x.Button;
      DXG.RequiredRule = x.RequiredRule;
      DXG.SearchPanel = x.SearchPanel;
      DXG.Sorting = x.Sorting;
      DXG.Column = x.Column;
      DXG.Paging = x.Paging;
      DXG.Editing = x.Editing;
      DXG.Lookup = x.Lookup;
      DXG.AsyncRule = x.AsyncRule;
      DXG.CustomRule = x.CustomRule;
    }),
    import('devextreme-react/autocomplete').then((x) => {
      DXG.Autocomplete = x.Autocomplete;
    }),
  ])
    .finally(() => (_isLoaded = true))
    .catch((e) => (_error = e));
}
